import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import {
  BiData,
  BiContextProvidedValue,
  ItemClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import CreateTierCard from '../../_partials/CreateTierCard';
import TierCard from '../../_partials/TierCard';

import s from './TierListDesktop.scss';

export interface Props {
  tiers?: PapiDeviation[];
  measuredCookieType?: MeasuredCookieType;
}

export const THUMB_HEIGHT = 95;
export const THUMB_WIDTH = 200;
export const THUMB_GAP = 16;
export const MAX_TIERS = 10;

const TierList: FC<Props> = ({ tiers, measuredCookieType }) => {
  const { t } = useTranslation();

  const { ref: containerRef, dimensions: measuredContainer } =
    useMeasureElementCookie(measuredCookieType, {
      cookieDim: 'width',
    });

  const containerWidth = useMemo(
    () => measuredContainer?.width,
    [measuredContainer?.width]
  );

  const thumbsPerRow = useMemo(() => {
    return Math.floor(containerWidth / (THUMB_WIDTH + THUMB_GAP));
  }, [containerWidth]);

  const gapWidth = THUMB_GAP * (thumbsPerRow - 1);

  const thumbWidth = useMemo(
    () => Math.floor((containerWidth - gapWidth) / thumbsPerRow),
    [containerWidth, gapWidth, thumbsPerRow]
  );

  const gridWidth = useMemo(
    () => thumbWidth * thumbsPerRow + gapWidth,
    [thumbsPerRow, thumbWidth, gapWidth]
  );

  if (!(tiers && tiers.length > 0)) {
    return null;
  }

  return (
    <BiLoggerContextProvider value={{ sectionname: 'tiers' }}>
      <div ref={containerRef}>
        <div style={{ width: gridWidth }}>
          <div className={s['header']}>
            <Heading className={s['heading']}>
              {t('studio.published.folderList.heading.tiers')}
            </Heading>
          </div>
          <div className={s['content']}>
            <div className={s['expanded-grid']}>
              {tiers.length < MAX_TIERS && (
                <CreateTierCard
                  width={thumbWidth}
                  height={THUMB_HEIGHT}
                  hasTiers
                />
              )}
              {tiers.map(tier => (
                <TierCard
                  key={`tier-${tier.deviationId}`}
                  tier={tier}
                  width={thumbWidth}
                  height={THUMB_HEIGHT}
                  biData={BiData<ItemClickBiEvent>({
                    evid: 100,
                    view: BiContextProvidedValue,
                    component: BiContextProvidedValue,
                    page_typeid: BiContextProvidedValue,
                    page_itemid: BiContextProvidedValue,
                    sectionname: BiContextProvidedValue,
                    typeid: ResourceType.TIER,
                    itemid: tier.deviationId,
                  })}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </BiLoggerContextProvider>
  );
};

TierList.displayName = 'TierList';
export default TierList;
